"use client";

import Link from "next/link";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Loader2 from "lucide-react/dist/esm/icons/loader";
import Mail from "lucide-react/dist/esm/icons/mail";
import ArrowLeft from "lucide-react/dist/esm/icons/arrow-left";
import CheckCircle from "lucide-react/dist/esm/icons/circle-check";
import AuthLayout from "@/components/auth/AuthLayout";
import {
  AuthFormPanel,
  AuthSkipLink,
  AuthFieldShell,
  AuthKeyboardHints,
} from "@/components/auth/auth-form-ui";
import { FormAnticipationBar } from "@/components/ui/loading-anticipation";
import { cn } from "@/lib/utils";
import { TurnstileWidget, isTurnstileEnabled } from "@/components/auth/TurnstileWidget";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [turnstileKey, setTurnstileKey] = useState(0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      setEmailError("Enter your email.");
      return;
    }
    if (!EMAIL_RE.test(trimmed)) {
      setEmailError("Enter a valid email address.");
      return;
    }
    if (isTurnstileEnabled() && !captchaToken) {
      setEmailError("Please complete the verification challenge.");
      return;
    }
    setEmailError(null);
    setLoading(true);

    const { error: resetError } = await supabase.auth.resetPasswordForEmail(trimmed, {
      redirectTo: `${window.location.origin}/reset-password`,
      ...(captchaToken ? { captchaToken } : {}),
    });

    if (resetError) {
      setCaptchaToken(null);
      setTurnstileKey((k) => k + 1);
      setEmailError(resetError.message);
      setLoading(false);
      return;
    }

    setSuccess(true);
    setLoading(false);
  };

  return (
    <>
      <AuthLayout
        heading="Forgot password"
        subheading="We&apos;ll email you a link to reset it"
      >
        {success ? (
          <AuthFormPanel className="animate-fade-up text-center" aria-live="polite">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-success/10">
              <CheckCircle className="h-7 w-7 text-success" />
            </div>
            <div className="space-y-2 text-left sm:text-center">
              <h3 className="font-semibold text-foreground text-sm">Check your email</h3>
              <p className="text-sm text-muted-foreground">
                If an account exists for <strong className="text-foreground">{email.trim()}</strong>, you&apos;ll get a
                reset link shortly. Check spam if you don&apos;t see it.
              </p>
            </div>
            <Button
              variant="outline"
              className="w-full h-11"
              onClick={() => {
                setSuccess(false);
                setEmail("");
              }}
            >
              Use a different email
            </Button>
            <p className="text-center">
              <Link
                href="/signin"
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to sign in
              </Link>
            </p>
          </AuthFormPanel>
        ) : (
          <>
            <AuthSkipLink href="#forgot-password-form">Skip to reset form</AuthSkipLink>
            <AuthFormPanel className="animate-fade-up stagger-1">
              <form
                id="forgot-password-form"
                onSubmit={(e) => void handleSubmit(e)}
                aria-label="Request password reset"
                className="space-y-4"
              >
                <AuthFieldShell>
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setEmailError(null);
                      }}
                      autoComplete="email"
                      autoFocus
                      aria-invalid={!!emailError}
                      className={cn(
                        "pl-10 bg-secondary border-0",
                        emailError && "border border-destructive/80 focus-visible:ring-destructive/30",
                      )}
                    />
                  </div>
                  {emailError && (
                    <p className="text-xs text-destructive" role="status">
                      {emailError}
                    </p>
                  )}
                </AuthFieldShell>

                <TurnstileWidget key={turnstileKey} onToken={setCaptchaToken} className="flex justify-center" />

                <FormAnticipationBar show={loading} label="Sending reset link" />

                <Button
                  type="submit"
                  className="w-full h-11"
                  disabled={loading || (isTurnstileEnabled() && !captchaToken)}
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
                </Button>
              </form>
              <AuthKeyboardHints />

              <p className="text-center border-t border-border/60 pt-4">
                <Link
                  href="/signin"
                  className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to sign in
                </Link>
              </p>
            </AuthFormPanel>
          </>
        )}
      </AuthLayout>
    </>
  );
}
